'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRelativeTime } from '@/hooks/useRelativeTime';
import { cn } from '@/lib/utils';
import { getBrowserWindow } from '@/lib/utils/browser-window';
import { formatCompactRelativeTime, toTimestamp } from '@/lib/utils/time';

type RelativeTimeStyle = NonNullable<Intl.RelativeTimeFormatOptions['style']>;

const DEFAULT_ABSOLUTE_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

interface RelativeTimeProperties {
  className?: string | undefined;
  date: Date | string | number | null | undefined;
  display?: 'default' | 'compact' | undefined;
  fallback?: string | undefined;
  initialNowMs?: number | undefined;
  locale?: string | undefined;
  minimumUpdateIntervalMs?: number | undefined;
  numeric?: Intl.RelativeTimeFormatOptions['numeric'] | undefined;
  /**
   * When true, we pause updates if the element scrolls off-screen to conserve timers.
   * Defaults to true.
   */
  pauseWhenOffscreen?: boolean | undefined;
  style?: RelativeTimeStyle | undefined;
  titleFormat?: Intl.DateTimeFormatOptions | undefined;
}

function useOnScreen<T extends Element>(pauseWhenOffscreen: boolean) {
  const observerReference = useRef<IntersectionObserver | null>(null);
  const isMountedReference = useRef(true);
  const [isVisible, setIsVisible] = useState(true);

  const cleanup = useCallback(() => {
    if (observerReference.current) {
      observerReference.current.disconnect();
      observerReference.current = null;
    }
  }, []);

  const updateVisibility = useCallback((next: boolean) => {
    if (!isMountedReference.current) {
      return;
    }
    setIsVisible((previous) => (previous === next ? previous : next));
  }, []);

  const setNode = useCallback(
    (node: T | null) => {
      cleanup();

      const win = getBrowserWindow();
      const ObserverCtor =
        win?.IntersectionObserver ?? (typeof IntersectionObserver === 'function' ? IntersectionObserver : null);

      if (!(pauseWhenOffscreen && ObserverCtor)) {
        updateVisibility(true);
        return;
      }

      if (!node) {
        return;
      }
      const observer = new ObserverCtor(([entry]) => {
        updateVisibility(entry?.isIntersecting ?? true);
      });

      observer.observe(node);
      observerReference.current = observer;
    },
    [cleanup, pauseWhenOffscreen, updateVisibility]
  );

  useEffect(
    () => () => {
      isMountedReference.current = false;
      cleanup();
    },
    [cleanup]
  );

  return { ref: setNode, isVisible } as const;
}

export function RelativeTime({
  date,
  className,
  locale,
  style = 'long',
  numeric = 'auto',
  titleFormat = DEFAULT_ABSOLUTE_FORMAT,
  initialNowMs,
  pauseWhenOffscreen = true,
  fallback = 'unknown time',
  minimumUpdateIntervalMs,
  display = 'default',
}: RelativeTimeProperties) {
  const timestamp = useMemo(() => toTimestamp(date), [date]);
  const isoString = useMemo(() => (timestamp === null ? null : new Date(timestamp).toISOString()), [timestamp]);
  const localizedTitle = useMemo(() => {
    if (timestamp === null) {
      return null;
    }

    const targetDate = new Date(timestamp);

    try {
      return new Intl.DateTimeFormat(locale, titleFormat).format(targetDate);
    } catch {
      return targetDate.toLocaleString();
    }
  }, [timestamp, locale, titleFormat]);

  const { ref, isVisible } = useOnScreen<HTMLTimeElement>(pauseWhenOffscreen);

  const { label, result, isValid } = useRelativeTime(timestamp ?? null, {
    locale,
    style,
    numeric,
    initialNowMs,
    active: pauseWhenOffscreen ? isVisible : true,
    minimumUpdateIntervalMs,
  });

  const displayLabel = useMemo(() => {
    if (!result) {
      return label;
    }
    if (display === 'compact') {
      return formatCompactRelativeTime(result);
    }
    return label;
  }, [display, result, label]);

  if (timestamp === null || !isValid) {
    return (
      <time
        ref={ref}
        className={cn('tabular-nums whitespace-nowrap', className)}
        title={localizedTitle ?? isoString ?? undefined}
      >
        {fallback}
      </time>
    );
  }

  return (
    <time
      ref={ref}
      className={cn('tabular-nums whitespace-nowrap', className)}
      dateTime={isoString ?? undefined}
      title={localizedTitle ?? isoString ?? undefined}
      suppressHydrationWarning
    >
      {displayLabel}
    </time>
  );
}
